import { collection, deleteDoc, doc, getDocs, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { ConfigRegrasClienteSchema } from '../models/config.schemas';
import type {
  ConfigCatalogoAcesso,
  ConfigTipoEquipamento,
  ConfigTipoSolicitacao,
  ConfigRegrasCliente,
} from '../models/config.types';

/**
 * Leitura das 4 collections de configuração do SI, e escrita de `si_config_regras_cliente`
 * (tela "Configurações", ver ConfiguracoesDrawer) — exclusivo de si_admin (firestore.rules
 * já restringia `write` nas 4 collections a `isSI()`, só faltava algum client escrever).
 * As outras 3 collections continuam só leitura por enquanto (sem tela própria ainda).
 */
class ConfigSIServiceClass {
  async getCatalogoAcesso(): Promise<ConfigCatalogoAcesso[]> {
    const snap = await getDocs(collection(db, 'si_config_catalogo_acesso'));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ConfigCatalogoAcesso);
  }

  async getTiposEquipamento(): Promise<ConfigTipoEquipamento[]> {
    const snap = await getDocs(collection(db, 'si_config_tipos_equipamento'));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ConfigTipoEquipamento);
  }

  async getTiposSolicitacao(): Promise<ConfigTipoSolicitacao[]> {
    const snap = await getDocs(collection(db, 'si_config_tipos_solicitacao'));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ConfigTipoSolicitacao);
  }

  async getRegrasCliente(): Promise<ConfigRegrasCliente[]> {
    const snap = await getDocs(collection(db, 'si_config_regras_cliente'));
    return snap.docs.map((d) => ({ ...d.data(), clienteId: d.id }) as ConfigRegrasCliente);
  }

  /** `merge: true` — cada tela de configuração escreve só o campo que lhe diz respeito
   * (ver comentário em ConfigRegrasCliente), nunca apaga o que outra tela já salvou. */
  async setRegrasCliente(clienteId: string, dados: Omit<ConfigRegrasCliente, 'clienteId'>): Promise<void> {
    const validated = ConfigRegrasClienteSchema.parse({ clienteId, ...dados });
    const { clienteId: _clienteId, ...campos } = validated;
    await setDoc(doc(db, 'si_config_regras_cliente', clienteId), campos, { merge: true });
  }

  /**
   * Usuário do GLPI que não é uma pessoa (ex.: "DEVOLVIDO PINE") e o que ele significa —
   * lembrado pra todas as importações do GLPI (ver importacaoGlpi.ts no servidor). `null`
   * desfaz (volta a ser tratado como pessoa).
   */
  async setUsuarioGlpi(
    login: string,
    config: {
      statusCustodia: string | null;
      situacaoDetalhe: string | null;
      clienteId: string | null;
      fornecedorManutencao: string | null;
      nome: string;
    } | null,
    ator: { id: string; name: string }
  ): Promise<void> {
    const ref = doc(db, 'si_config_usuarios_glpi', login);
    if (!config) {
      await deleteDoc(ref);
      return;
    }
    await setDoc(ref, { ...config, naoEPessoa: true, atualizadoPor: ator, atualizadoEm: serverTimestamp() });
  }

  /** Pessoa do GLPI que não casou sozinha (nome/login diferente do Digital) → qual colaborador é.
   * Mesmo documento de setUsuarioGlpi (um login tem uma marcação só); desfazer = setUsuarioGlpi(login, null). */
  async setUsuarioGlpiColaborador(
    login: string,
    vinculo: { colaboradorId: string; colaboradorNome: string; nome: string },
    ator: { id: string; name: string }
  ): Promise<void> {
    await setDoc(doc(db, 'si_config_usuarios_glpi', login), {
      ...vinculo,
      naoEPessoa: false,
      atualizadoPor: ator,
      atualizadoEm: serverTimestamp(),
    });
  }
}

export const configSIService = new ConfigSIServiceClass();
